<template>
  <div id="app" :data-app-version="appVersion">
    <transition mode="out-in">
      <router-view/>
    </transition>
    <portal-target name="modal1"></portal-target>
    <!-- 重ねて同時に表示する際は以下を使用-->
    <portal-target name="modal2"></portal-target>
    <!-- メッセージポップアップ -->
    <div class="message-popup" v-if="$store.state.status.messagePopup.isShow">
      <modal @close="hideMessagePopup">
        <template slot="title">{{ $store.state.status.messagePopup.title }}</template>
        <template slot="name">
          <p v-for="(message, index) in $store.state.status.messagePopup.messages" :key="index">{{ message }}</p>
        </template>
        <template slot="submit">
          <div class="button-box">
            <button type="button" @click="hideMessagePopup" class="cancel normal-btn">閉じる</button>
          </div>
        </template>
      </modal>
    </div>
  </div>
</template>

<script>
import Modal from '@/components/Util/Modal'

const SESSION_SECONDS = 7200
const SESSION_CHECK_SECONDS = 30

export default {
  components: {
    Modal
  },
  data () {
    return {
      isShowAppUpdateInfo: true,
      sessionCheckInterval: null,
    }
  },
  computed: {
    appVersion () {
      return process.env.APP_VERSION
    }
  },
  async mounted () {
    const updateInfo = localStorage.getItem('appUpdate')
    updateInfo && await this.showAppUpdateInfo(updateInfo)

    this.sessionCheck()
    this.startSessionCheckInterval()

    document.addEventListener('click', this.updateExpireTime)
    document.addEventListener('keydown', this.updateExpireTime)

    this.setPageVisibilitySessionCheck()
  },
  methods: {
    setPageVisibilitySessionCheck () {
      // PCのスリープから戻ったときなど、ページのアクティブが変更された時にもセッションチェックするように
      // 参考 https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
      let visibilityChange = null
      if (typeof document.hidden !== 'undefined') { // Opera 12.10 and Firefox 18 and later support
        visibilityChange = 'visibilitychange'
      } else if (typeof document.msHidden !== 'undefined') {
        visibilityChange = 'msvisibilitychange'
      } else if (typeof document.webkitHidden !== 'undefined') {
        visibilityChange = 'webkitvisibilitychange'
      }
      document.addEventListener(visibilityChange, this.sessionCheck)
    },
    async showAppUpdateInfo (info) {
      this.isShowAppUpdateInfo = true
      info = JSON.parse(info)
      const json = await this.$http({
        url: '/status',
        type: 'GET',
      })
      if (!json || json.status !== 200 || json.appVersion !== this.appVersion) {
        // ノーキャッシュリロードアップデートしたが、レスポンスがおかしい or バージョンが揃わない時
        this.$store.commit('mergeMessagePopup', {
          isShow: true,
          isSuccess: false,
          title: '',
          messages: [
            `アップデートに失敗しました(${this.appVersion} → ${json.appVersion})`,
            '通信状況を確認して時間を置いてから、画面をクリックして再実行してください'
          ]
        })
        document.body.addEventListener('click', () => location.reload(true))
        return
      }

      let versionInfo = null
      if (info.beforeVersion !== json.appVersion) {
        versionInfo = `${info.beforeVersion} → ${json.appVersion}`
      } else {
        // ローカルストレージにバージョンアップデートフラグを持っている状態で、バージョン切り戻しを行った際
        versionInfo = json.appVersion
      }
      this.$store.commit('mergeMessagePopup', {
        isShow: true,
        isSuccess: true,
        title: '',
        messages: [`最新版にアップデートされました(${versionInfo})`]
      })
      // 表示したら削除する
      localStorage.removeItem('appUpdate')
      this.isShowAppUpdateInfo = false
    },
    hideMessagePopup () {
      this.$store.commit('mergeMessagePopup', {
        isShow: false
      })
    },
    updateExpireTime () {
      // 同時にタブを開いていることも考慮してlocalStorageに入れる
      localStorage.setItem('expireTime', Date.now() + SESSION_SECONDS * 1000)
    },
    clickToLogin () {
      this.$router.push({ name: 'login', query: { redirect: this.$route.path } })
    },
    startSessionCheckInterval () {
      // setTimeoutだとPCのスリープ時にも止まってしまうため定期チェックする
      // App.vueがdestoryされない限り、常時実行する
      this.sessionCheckInterval = setInterval(this.sessionCheck, SESSION_CHECK_SECONDS * 1000)
    },
    sessionCheck () {
      if (this.$route.meta && this.$route.meta.isPublic) {
        // トークン不要な画面はスキップ
        return
      }
      let expireTime = localStorage.getItem('expireTime')
      if (!expireTime) {
        return this.updateExpireTime()
      }
      // 検証用に残す
      // console.log(`クライアントセッション: あと ${(Number(expireTime) - Date.now()) / 1000}秒`)
      if (Date.now() > Number(expireTime)) {
        // alertだと 同一ドメインで複数タブでalertが出せないらしく、独自ポップアップで対応
        this.$store.commit('showErrorMessagePopup', ['セッションの有効期限が切れました'])
        this.$utils.token.remove()
        // expireTimeを削除すると、別タブのクライアントセッションがリセットされてしまうので注意
        document.body.addEventListener('click', this.clickToLogin, { once: true })
      }
    },
  },
  watch: {
    $route (to, from) {
      if (this.isShowAppUpdateInfo) return
      // ブラウザバックでメッセージが表示され続ける問題対応
      this.$store.state.status.messagePopup.isShow && this.hideMessagePopup()
    },
  },
}
</script>

<style lang="scss">
// グローバルscssを一度だけインポートする
@import '@/scss/app/app.scss';
body {
  overflow: hidden;
}
#app {
  height: 100vh;
  .message-popup {
    position: relative;
    z-index: 5001;
  }
}
</style>
<style lang="scss" scoped>
.v-enter-active, .v-leave-active {
  transition: opacity .5s;
}
.v-enter, .v-leave-to {
  opacity: 0;
}
.button-box {
  button {
    &.cancel {
      border: solid 1px #c6c6c6;
    }
  }
}
</style>
